import { AlertTriangle, Loader2, Zap } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createWorkTask, uploadWorkFile } from '../../api/workApi'
import { useAuth } from '../../contexts/AuthContext'
import type { WorkSubcategoryInfo, WorkWizardState } from '../../types/work'

interface WorkWizardStep4Props {
  state: WorkWizardState
  subcategoryInfo?: WorkSubcategoryInfo
  onBack: () => void
}

const CATEGORY_LABELS: Record<string, string> = {
  audio_processing: 'Audio Processing',
  image_generation: 'Image Generation',
  content_writing: 'Content & Writing',
  code: 'Code',
  data_analysis: 'Data Analysis',
}

export default function WorkWizardStep4({ state, subcategoryInfo, onBack }: WorkWizardStep4Props) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const estimatedCost = state.estimate?.flz_cost ?? 0
  const balance = user?.wallet_balance ?? 0
  const insufficient = balance < estimatedCost

  async function handleSubmit() {
    if (!state.category || !state.subcategory || !state.title.trim()) {return}
    setLoading(true)
    setError(null)

    try {
      const res = await createWorkTask({
        title: state.title.trim(),
        description: state.description || undefined,
        category: state.category,
        subcategory: state.subcategory,
        input_text: state.input_text || undefined,
        input_params: Object.keys(state.input_params).length > 0 ? state.input_params : undefined,
      })

      // Upload file if present
      if (state.file) {
        await uploadWorkFile(res.task_id, state.file)
      }

      navigate(`/work/${res.task_id}`)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create job'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Summary */}
      <div
        style={{
          padding: '16px',
          borderRadius: '12px',
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
          Job Summary
        </div>
        <Row label="Category" value={CATEGORY_LABELS[state.category ?? ''] ?? state.category ?? '—'} />
        <Row label="Task Type" value={subcategoryInfo?.label ?? state.subcategory ?? '—'} />
        <Row label="Title" value={state.title} />
        {state.file && <Row label="File" value={state.file.name} />}
        {subcategoryInfo?.model && (
          <Row label="AI Model" value={subcategoryInfo.model.split('/').pop() ?? subcategoryInfo.model} mono />
        )}
      </div>

      {/* FLZ cost */}
      <div
        style={{
          padding: '16px',
          borderRadius: '12px',
          background: 'rgba(99, 102, 241, 0.08)',
          border: '1px solid rgba(99, 102, 241, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted, #6b7280)', marginBottom: '2px' }}>Estimated Cost</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--primary, #6366f1)', lineHeight: 1 }}>
            {estimatedCost.toFixed(2)} <span style={{ fontSize: '14px', fontWeight: 600 }}>FLZ</span>
          </div>
        </div>
        <Zap size={32} style={{ color: 'var(--primary, #6366f1)', opacity: 0.4 }} />
      </div>

      {/* Balance */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderRadius: '10px',
          background: insufficient ? 'rgba(239, 68, 68, 0.06)' : 'var(--card-bg)',
          border: `1px solid ${insufficient ? 'rgba(239, 68, 68, 0.25)' : 'var(--border-color)'}`,
        }}
      >
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Your Balance</span>
        <span
          style={{
            fontSize: '15px',
            fontWeight: 700,
            color: insufficient ? 'var(--danger, #ef4444)' : 'var(--text-primary)',
          }}
        >
          {balance.toFixed(2)} FLZ
        </span>
      </div>

      {/* Insufficient balance warning */}
      {insufficient && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            padding: '12px 14px',
            borderRadius: '10px',
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            fontSize: '13px',
            color: 'var(--danger, #ef4444)',
          }}
        >
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
          <div>
            Insufficient balance. You need {(estimatedCost - balance).toFixed(2)} more FLZ.{' '}
            <a href="/wallet" style={{ color: 'var(--danger, #ef4444)', fontWeight: 600 }}>
              Top up wallet →
            </a>
          </div>
        </div>
      )}

      {/* API error */}
      {error && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            fontSize: '13px',
            color: 'var(--danger, #ef4444)',
          }}
        >
          {error}
        </div>
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button
          onClick={onBack}
          disabled={loading}
          style={{
            padding: '10px 20px',
            borderRadius: '10px',
            background: 'transparent',
            border: '1px solid var(--border-color)',
            color: 'var(--text-secondary)',
            fontSize: '14px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1,
          }}
        >
          ← Back
        </button>
        <button
          onClick={() => { void handleSubmit() }}
          disabled={loading || insufficient}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 24px',
            borderRadius: '10px',
            background: loading || insufficient ? 'var(--border-color)' : 'var(--primary, #6366f1)',
            color: loading || insufficient ? 'var(--text-muted, #6b7280)' : '#fff',
            border: 'none',
            fontSize: '14px',
            fontWeight: 600,
            cursor: loading || insufficient ? 'not-allowed' : 'pointer',
          }}
        >
          {loading && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
          {loading ? 'Starting...' : 'Start Job'}
          <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
        </button>
      </div>
    </div>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
      <span style={{ fontSize: '12px', color: 'var(--text-muted, #6b7280)', flexShrink: 0 }}>{label}</span>
      <span
        style={{
          fontSize: '13px',
          color: 'var(--text-primary)',
          fontFamily: mono ? 'monospace' : undefined,
          textAlign: 'right',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '240px',
        }}
      >
        {value}
      </span>
    </div>
  )
}
